// Flow-engine readiness (U12): the run-replay transport the flow-engine's M2
// timeline views sit on top of. The component (Ui::Flow::TimelineScrubber)
// renders the chrome — slider over the recorded op sequence, step buttons,
// readout; dragging or stepping emits `ui--flow-timeline:seek` with
// { seq, canvasId }. The HOST owns what a seq means: it fetches the frame
// (frames_url is carried as a data value for the host's JS, and for the full
// player a later card will build on this controller) and applies it to the
// canvas identified by canvas_id. This minimal form does NOT fetch frames
// itself — scrubbing is a pure event emitter by design.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["slider", "readout"]

  static values = {
    // The canvas the frames replay onto — rides every seek event so a host
    // with several canvases can route the frame to the right engine.
    canvasId: String,
    // Where the recorded frames live (emitted as a data value only; the
    // minimal form never fetches it itself).
    framesUrl: String,
    // Highest recorded seq — the slider's max and the forward clamp.
    maxSeq: { type: Number, default: 0 }
  }

  connect() {
    this._syncSliderBounds()
    this._updateReadout()
  }

  // A late max_seq (host streams the recording progressively) re-bounds the
  // slider; no seek is emitted — a bounds change is not a scrub.
  maxSeqValueChanged() {
    this._syncSliderBounds()
    this._updateReadout()
  }

  // Slider input → live seek. `input` fires continuously while dragging; a
  // host that wants coarser frames debounces on its side.
  seekFromSlider(event) { this.seek(event.target.value) }

  stepBack()    { this.seek(this.seq - 1) }
  stepForward() { this.seek(this.seq + 1) }

  get seq() { return this.hasSliderTarget ? Number(this.sliderTarget.value) || 0 : 0 }

  // Programmatic seek (public API): clamps to [0, maxSeq], moves the slider,
  // re-reads the readout, emits. The gestures route through here so clamp +
  // readout + emission can never drift apart.
  seek(seq) {
    const next = Math.min(Math.max(0, Math.round(Number(seq) || 0)), Math.max(0, this.maxSeqValue))
    if (this.hasSliderTarget) this.sliderTarget.value = `${next}`
    this._updateReadout()
    this._emitSeek(next)
  }

  _syncSliderBounds() {
    if (!this.hasSliderTarget) return
    this.sliderTarget.setAttribute("min", "0")
    this.sliderTarget.setAttribute("max", `${Math.max(0, this.maxSeqValue)}`)
  }

  _updateReadout() {
    if (!this.hasReadoutTarget) return
    this.readoutTarget.textContent = `${this.seq} / ${Math.max(0, this.maxSeqValue)}`
  }

  _emitSeek(seq) {
    this.element.dispatchEvent(new CustomEvent("ui--flow-timeline:seek", {
      detail: { seq, canvasId: this.canvasIdValue },
      bubbles: true
    }))
  }
}
